import { Component, OnInit, ViewChild, TemplateRef, Input } from '@angular/core';
import { FeeBreakdownData } from 'src/app/components/shared/info-icon/info-icon.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { OrderObject } from 'src/app/models/order-list.model';
import { CartService } from 'src/app/services/cart/cart.service';
import { BeatsService } from 'src/app/services/beats-service/beats.service';
import { DownloadableReadUrlType } from 'src/app/models/selected-files-data';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';
import { Ga4trackingService } from 'src/app/services/ga4Tracking/ga4tracking.service';
import { MobileVerificationFlowService } from 'src/app/services/Auth/mobile-verification-flow.service';

@Component({
  selector: 'ipm-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {
  @Input() orderData: any = null;
  @Input() isInlineView: boolean = false;
  @Input() viewType: string = "myOrder";
  
  currentType: string = "";
  ordersData: any = "";
  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  selectedIssue: string = '';
  isMobileExpanded: boolean = false;
  @ViewChild('helpSupportDialog', { static: false }) helpSupportDialog: TemplateRef<any>;
  @ViewChild('submitPopup', {static: false}) submitPopupTemplate :  TemplateRef<any>;
  @ViewChild('orderHelpSupportTemplate', { static: false }) orderHelpSupportDialog: TemplateRef<any>;
  @ViewChild('otpTemplate', { static: false }) otpTemplate: TemplateRef<any>;
  
  OTPInputData: any = {};
  errorMessage: string = '';

  constructor(
    private activateRoute: ActivatedRoute,
    private router: Router,
    public commonService: CommonService,
    private cartService: CartService,
    private beatService: BeatsService,
    private browserStorageService: BrowserStorageService,
    private ga4Tracking: Ga4trackingService,
    private mobileVerificationFlow: MobileVerificationFlowService) { }

  
  
  /**
   * Builds a typed fee breakdown data object to be passed to the ipm-info-icon component.
   * The template in InfoIconComponent renders the actual UI — no HTML strings here.
   * @param data order data object
   * @returns FeeBreakdownData
   */
  getFeeBreakdownData(data: any): FeeBreakdownData {
    if (this.currentType === 'myOrder') {
      return {
        type: 'myOrder',
        subTotal: data.sub_total || 0,
        discount: data.discount || 0,
        platformFee: data.convenice_and_safety_fee || 0,
        total: data.total || 0
      };
    }

    // Studio Order - Detailed Bifurcation
    const illpeopleRoyaltiesFee = data.illpeople_royalties_fee_exlusive_tax || 0;
    const commissionTotal = illpeopleRoyaltiesFee;
    const gatewayFeeTotal = data.gateway_fee_exlusive_tax || 0;

    const preTaxFeesTotal = commissionTotal + gatewayFeeTotal;
    const gst = preTaxFeesTotal * 0.18;
    const totalDeducted = commissionTotal + gatewayFeeTotal + gst;

    const studioBreakdown = {
      type: 'studioOrder',
      revenue: data.items_total_price || 0,
      commissionTotal,
      gatewayFeeTotal,
      gst,
      totalDeducted,
      earnings: data.ammount_transfer_after_fees_and_tax_deductions || 0
    };

    return studioBreakdown as FeeBreakdownData;
  }

  ngOnInit(): void {
    // If orderData is provided via @Input, use it directly
    if (this.orderData) {
      this.ordersData = this.orderData;
      this.currentType = this.viewType;
      if (this.ordersData && this.ordersData.items) {
        this.updateOrderData();
      }
      return;
    }

    // Otherwise, load from route params (original behavior)
    this.activateRoute.data.subscribe((data: any) => {
      this.commonService.setTitleService(data['title']);
      this.currentType = data['type'];
    });

    this.activateRoute.params.subscribe((param: any) => {
      let orderId = param.id;
      if (orderId) {
        this.getParticularOrderId(orderId);
      }
    });

    // Check if we need to show the guest OTP popup
    const historyState = history.state;
    if (historyState && historyState.showGuestOtp) {
      const email = historyState.email || '';
      const guestUserId = historyState.guestUserId || '';
      setTimeout(() => {
        this.openOtpPopup(email, guestUserId);
      }, 500); // slight delay to allow rendering
    }
  }


  goBack(): void {
    let router = this.currentType === 'myOrder' ? 'orders' : 'studio-orders';
    this.router.navigate(['/'+router]);
  }

  private openOtpPopup(email: string, guestUserId: string) {
    this.OTPInputData = {
      email: email,
      _id: guestUserId,
      callingForm: 'guest-checkout'
    };
    this.errorMessage = '';
    const className = this.commonService.getClassName('ipm-otp-model');
    this.commonService.openModel(this.otpTemplate, this.OTPInputData, className, true);
  }

  openHelpSupportDialog(senDataPopup: any = ""): void {
    let sendData: any = ""
    if (senDataPopup) {
      sendData = senDataPopup;
    } else {
      sendData = {currentOrder: this.ordersData,  "type": 'sellerSupport'};
    }
    let className = this.commonService.getClassName('ipm-help-support-dialog');
    this.commonService.openModel(this.helpSupportDialog, sendData, className, false);
  }

  /**
   * Fetch particular order data from backend
   * @param orderId string
   */
  getParticularOrderId(orderId: string): void {
    if (this.currentType === 'studioOrder') {
      this.getStudioOrderData(orderId);
    } else {
      this.getRegularOrderData(orderId);
    }
  }

  /**
   * Fetch regular order data (buyer orders)
   * @param orderId string
   */
  getRegularOrderData(orderId: string): void {
    this.cartService.getParticularOrderData(orderId).subscribe((orderRes: Array<OrderObject>) => {
      if (orderRes.length > 0 ) {
        this.ordersData = orderRes[0];
        this.updateOrderData();
      }
    }, (error) => {
      this.commonService.showToastError(error);
    });
  }

  /**
   * Fetch studio order data (seller orders)
   * @param orderId string
   */
  getStudioOrderData(orderId: string): void {
    const sendData = { sale_id: orderId, page: { offset: 0, limit: 1 } }; // Get more data to find the order
    this.cartService.getSaleList(sendData, true, "single").subscribe((sellerDataObj: any) => {
     this.ordersData = sellerDataObj?.data;
     this.ordersData['status'] = this.ordersData?.payment_transfer_status;
     this.ordersData['number'] = this.ordersData?.order?.number; 
     this.ordersData['feeCharges'] = ((this.ordersData?.gateway_fee_exlusive_tax || 0) + (this.ordersData?.illpeople_royalties_fee_exlusive_tax || 0)) * 1.18;
     this.updateOrderData();
    }, (error) => {
      this.commonService.showToastError(error);
    });
  }

  updateOrderData() {
    let orderItem: any = this.ordersData.items;
      orderItem.map((item: any, index: number) => {
        if (item.beat) {
          item['thumbnail'] = item.beat?.cover_picture;
          item['title'] = item.beat?.title;
          item['tag'] = "Beat";
          item['artist'] = item?.seller?.producer?.[0]?.store?.general?.name;
          // For studioOrder, use order-level beat_licence array by index; for myOrder, use item-level
          item['licenseDownloadUrl'] = this.currentType === 'studioOrder' ? this.ordersData.beat_licence?.[index]?.file : item.beat_licence?.[0]?.file;
          item['licenseTitle'] = item.licence?.title;
          item['feeCharges'] = 0;
          item['serial_number'] = item.beat?.serial_number;
        } else {
          item['thumbnail'] = item.sound?.coverImageUrl;
          item['title'] = item.sound?.title;
          item['tag'] = "Sound Kits";
          item['artist'] = item.sound?.author?.name;
          // For studioOrder, use order-level sound_licence array by index; for myOrder, use item-level
          item['licenseDownloadUrl'] = this.currentType === 'studioOrder' ? this.ordersData.sound_licence?.[index]?.file : item.sound_licence?.[0]?.file;
          item['licenseTitle'] = "Non Exclusive"
          item['feeCharges'] =  this.currentType === 'studioOrder' ?  this.ordersData?.ammount_transfer_after_fees_and_tax_deductions : item.sound?.licenseFee;
          item['serial_number'] = item.sound?.serial_number;
        }
    });
    this.ordersData.items = orderItem;
  }

  closePopup() {
    this.commonService.closePopUp();
  }

  submitEvent($event: any) {
    if ($event) {
      this.closePopup();
      this.openSubmitModel($event)
    }
  }
  

  openSubmitModel(ticketNumb: any) {
    let className =  this.commonService.isMobileDisableClick ?  'ipm-mobile-view-confirm-popup' : 'ipm-desktop-view-confirm-popup';
    const sendData = {
      ticketNumber: ticketNumb
    };
    this.commonService.openModel(this.submitPopupTemplate, sendData , className);
  }

   downloadLiceFiles(fileUrl: string, type: string) {
    this.commonService.downloadFile(fileUrl, type);
    let eventName = "donwload_buyer_beat_lic";
    let attributes: any = {
      category: 'Beats',
      label: 'button',
      action: 'click',
      userRole: this.browserStorageService?.getCurrentRoles || 'guest',
      userId : this.browserStorageService?.getUserId || 'guest'
      
    };
    this.ga4Tracking.sendEvent(eventName, attributes);
  }

  getReadableUrl(beatData:any, type: string) {
    if (beatData) {
      // ── MP3: API returns binary blob directly, not a pre-signed URL ──────────
      if (type === 'mp3') {
        const sendData = { beat_id: beatData?.beat?._id, beat_file_type: 'mp3' };
        const fileName = (beatData?.beat?.title || 'download').replace(/\s+/g, '_').toLowerCase() + '.mp3';

        this.beatService.getMp3Blob(sendData).subscribe(
          (blob: Blob) => {
            this.commonService.downloadBlob(blob, fileName);
          },
          (error: any) => {
            if (this.mobileVerificationFlow.triggerFromError(error, 'download', false)) {
              return;
            }
            this.commonService.showToastError(error);
          }
        );

        const attributes: any = {
          category: 'Beats',
          label: 'button',
          action: 'click',
          userRole: this.browserStorageService?.getCurrentRoles || 'guest',
          userId: this.browserStorageService?.getUserId || 'guest',
          Genre: beatData?.beat?.genre || 'N/A',
          BeatName: beatData?.beat?.name || 'N/A'
        };
        this.ga4Tracking.sendEvent('download_mp3', attributes);
        return;
      }

      // wav and stems files 
      let readType = type === 'wav'? DownloadableReadUrlType.Wav : DownloadableReadUrlType.Stem; 
      let eventName = type === 'wav' ? 'download_wav' : 'download_stem';
      let attributes: any = {
        category: 'Beats',
        label: 'button',
        action: 'click',
        userRole: this.browserStorageService?.getCurrentRoles || 'guest',
        userId : this.browserStorageService?.getUserId || 'guest' ,
        Genre : beatData?.beat?.genre || 'N/A',
        BeatName : beatData?.beat?.name || 'N/A'
      };
      this.ga4Tracking.sendEvent(eventName, attributes);
      if (beatData.tag === "Beat") {
         let sendData = {
            beat_id: beatData?.beat?._id,
            beat_file_type: readType,
          };
        this.beatService.getReadBaseUrl(sendData).subscribe( res => {
          if (res.data) {
            this.commonService.downloadFile(res.data.url, type, beatData.beat);
          }
        }, (error: any) => {
          if (this.mobileVerificationFlow.triggerFromError(error, 'download', false)) {
            return;
          }
          this.commonService.showToastError(error);
        });
      } else {
         let sendData: any = {
            sound_id: beatData?.sound?._id,
            file_type: type,
          };
        this.beatService.getReadBaseUrl(sendData,true,'sound').subscribe( res => {
          if (res.data) {
            this.commonService.downloadFile(res.data.url, type, beatData.beat);
          }
        }, (error: any) => {
          if (this.mobileVerificationFlow.triggerFromError(error, 'download', false)) {
            return;
          }
          this.commonService.showToastError(error);
        });
      }
    }
  }

  showClose(data: any) {
  }

  supportOpen() {
    this.commonService.openSupport();
  }

  openHelpSupport(currentData: any = "") {
    // let currentRoute: any =  this.router.url; 
    // if (currentRoute.includes('studio-orders')){
    //   currentData['orderType'] = 'studioOrder';
    // }
    this.openSupportOrderPopup(this.ordersData, currentData);
  }

  openSupportOrderPopup(currentOrderDetails: any, currentBeat: any) {
    if (currentOrderDetails) {
      let sendData = {currentOrder: currentOrderDetails, currentBeatData: currentBeat}
      let className = this.commonService.getClassName('ipm-order-help-support-dialog');
      this.commonService.openModel(this.orderHelpSupportDialog, sendData, className, false);
    }
  }

  onOptionEventTrigger($event: any) {
    if ($event) {
      let optionData: any = {
       "currentOrder": $event?.orderDetails,
       "selectedOption": $event?.selectedOption,
       "type": this.currentType === 'studioOrder' ? 'sellerSupport' : 'orderHelpSupport'
      };
      this.openHelpSupportDialog(optionData);
      // this.submitEvent($event)
    }
  }
  //Temporary fix for the studio orders api lacking 
  gotoParticularItem(Item: any) {
      let serialNumber = Item?.serial_number;

      if (!serialNumber && this.currentType === 'studioOrder') {
        const result = this.extractIdFromUrl(Item.thumbnail || Item.licenseDownloadUrl);
        if (result) {
          serialNumber = result.id;
        }
      }
      let url = Item?.tag === 'Beat' ? "/bt-" + serialNumber : "/sk-" + serialNumber;
      this.router.navigate([url]);
    }
  gotoParticularArtist(Item:any) {
    let artisUrl = `/${Item?.artist[0] === '@' ? Item?.artist : '/@' + Item?.artist}`;
    this.router.navigate([artisUrl]);
  }
  //Temporary fix for the studio orders api lacking 
  extractIdFromUrl(url: string): { type: 'beat' | 'sound', id: string } | null {
    if (!url) return null;
    // Match patterns like beat-100033, sound-101485, or sample-101485
    const match = url.match(/\/(beat|sound|sample)-([0-9]+)/i);
    if (match) {
      const type = match[1].toLowerCase() === 'beat' ? 'beat' : 'sound';
      return {
        type: type,
        id: match[2]
      };
    }
    return null;
  }
       
  preventDefault(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
  }
}
